import React, { createContext, useCallback, useContext, useState } from 'react';

const STORAGE_KEY = 'heralabx_seasons';

// Season cutover is June 15, not the calendar new year or July 1 — pre-season
// training (e.g. "1F PS" scenarios) starts in mid/late June and counts toward
// the upcoming season, not the one that just finished.
export function dateToSeason(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).slice(0, 10).split('-').map(Number);
  if (!y || !m) return null;
  const sy = (m > 6 || (m === 6 && d >= 15)) ? y : y - 1;
  return `${sy}-${String(sy + 1).slice(-2)}`;
}

function currentSeason() {
  return dateToSeason(new Date().toISOString().slice(0, 10));
}

function computeAvailableSeasons() {
  const cur = currentSeason();
  const [sy] = cur.split('-').map(Number);
  return [`${sy - 1}-${String(sy).slice(-2)}`, cur];
}

export function seasonLabel(s) {
  return `${s.slice(2, 4)}/${s.slice(5)}`;
}

const SeasonContext = createContext(null);

export function SeasonProvider({ children }) {
  const [selectedSeasons, setSelectedSeasons] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [currentSeason()];
    } catch {
      return [currentSeason()];
    }
  });

  const update = seasons => {
    setSelectedSeasons(seasons);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seasons));
  };

  const isInSeason = useCallback(
    dateStr => {
      if (!dateStr) return true;
      const s = dateToSeason(dateStr);
      return !s || selectedSeasons.length === 0 || selectedSeasons.includes(s);
    },
    [selectedSeasons],
  );

  return (
    <SeasonContext.Provider
      value={{
        selectedSeasons,
        setSelectedSeasons: update,
        isInSeason,
        availableSeasons: computeAvailableSeasons(),
      }}
    >
      {children}
    </SeasonContext.Provider>
  );
}

export const useSeason = () => useContext(SeasonContext);
